import gql from 'graphql-tag';
import { bundle, workspaceRef, workspaceSectionRef } from './bundle';
import { business } from './business';
import { message as chatMessage, readMarker as chatReadMarker } from './chat';
import { accessLevel, collectionViewUser, short as shortUser } from './user';

// Re-export for backwards compatibility
export { workspaceRef, workspaceSectionRef };

export const report = gql`
  fragment ChannelReport on Report {
    id
    name
    source
    metabaseEmbedPath
    omniContentId
    updatedAt
    createdAt
    workflowTemplateId
  }
`;

export const group = gql`
  fragment ChannelGroup on Group {
    id
    name
    description
  }
`;

export const membership = gql`
  fragment ChannelMembership on ChannelMembership {
    id
    readCardCount
    accessLevel {
      ...AccessLevel
    }
    configAccessLevelType
    user {
      id
      isMe
    }
  }

  ${accessLevel}
`;

export const assignableMembership = gql`
  fragment AssignableChannelMembership on ChannelMembership {
    id
    readCardCount
    accessLevel {
      ...AccessLevel
    }
    user {
      ...CollectionViewUser
    }
  }

  ${accessLevel}
  ${collectionViewUser}
`;

export const membershipV2 = gql`
  fragment ChannelMembershipV2 on ChannelMembership {
    id
    userId
    readCardCount
    accessLevel {
      ...AccessLevel
    }
    configAccessLevelType
  }

  ${accessLevel}
`;

export const settings = gql`
  fragment ChannelSettings on Channel {
    id
    slug
    name
    description
    imageUrl
    type
    workflowTemplateId
    readAt
    notificationSettings
    snoozeUntil
    bundle {
      ...Bundle
    }
  }

  ${bundle}
`;

export const groups = gql`
  fragment ChannelGroups on Channel {
    id
    slug
    groups {
      ...ChannelGroup
    }
  }

  ${group}
`;

export const chatThread = gql`
  fragment ChannelChatThread on Channel {
    id
    slug
    name
    description
    type
    readAt
    imageUrl
    imgs
    createdAt
    business {
      ...Business
    }
    userReadMarkers {
      ...ChatUserReadMarker
    }
    isTyping {
      ...ShortUser
    }
    groups {
      ...ChannelGroup
    }

    # connection makes fetchMore easy using React Apollo (for pagination)
    messages(
      since: $since
      limit: $limit
      # If next is true (or not set), we will fetch messages _older_
      # than $since. Otherwise we will fetch newer messages. Fetching
      # newer messages ($fetchMessagesAfterSince = false) is mainly
      # used when jumping to a specific message in search.
      next: $fetchMessagesAfterSince
    ) @connection(key: "messages") {
      ...ChatMessage
    }
    visible
  }

  ${group}
  ${shortUser}
  ${chatMessage}
  ${business}
  ${chatReadMarker}
`;

export const channelUnread = gql`
  fragment ChannelUnread on Channel {
    id
    unreadCount
  }
`;

export const unreadCounts = gql`
  fragment ChannelUnreadCounts on Channel {
    id
    slug
    unreadCount
    unreadMessagesCount: unreadCount(filter: MESSAGES)
  }
`;

export const listItem = gql`
  fragment ChannelListItem on Channel {
    id
    slug
    name
    workflowTemplateId
    type
    imageUrl
    description
    updatedAt
    deletedAt
    readAt
    unreadCount
    unreadMessagesCount: unreadCount(filter: MESSAGES)
    cardCount
    defaultFocus
    messages(limit: 1) {
      ...ChatMessage
    }
    autoAddUsers
    visible
  }

  ${chatMessage}
  ${shortUser}
`;

export const deletedChannel = gql`
  fragment DeletedChannel on Channel {
    id
    slug
    name
    workflowTemplateId
    type
    imageUrl
    updatedAt
    deletedAt
  }
`;

export const channel = gql`
  fragment Channel on Channel {
    id
    slug
    type
    workflowTemplateId
    name
    imageUrl
    createdAt
    cardCount
    visible
    description
    defaultFocus
    bundle {
      ...Bundle
    }
  }

  ${bundle}
`;

export const channelCreate = gql`
  fragment ChannelCreate on Channel {
    id
    slug
    workflowTemplateId
  }
`;

export const workspaceSection = gql`
  fragment WorkspaceSection on WorkspaceSection {
    id
    title
    createdAt
    updatedAt
    channelAssociations {
      channelId
      workspaceSectionId
    }
    bundle {
      ...Bundle
    }
  }

  ${bundle}
`;

export const sharedCardLink = gql`
  fragment SharedCardLink on SharedCardLink {
    id
    type
    channelId
    userId
    cardId
    viewTemplateId
    url
    shortUrl
  }
`;
